import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Col, Flex, Row, Statistic, Table, Tag, Typography } from "antd";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { SupplierSelect } from "../procurement/common";

export type BillRow = Schemas["BillRow"];
export type BillStatus = Schemas["BillStatus"];
type Aging = Schemas["Payables"]["aging"][number];

const STATUS_CHOICES = ["unpaid", "draft", "overdue", "paid", "void", "all"] as const;
export const BILL_STATUSES: BillStatus[] = ["draft", "open", "partial", "paid", "void"];

const statusColor: Record<BillStatus, string> = { draft: "gold", open: "blue", partial: "cyan", paid: "success", void: "default" };

export function BillStatusTag({ status, overdue }: { status: BillStatus; overdue?: boolean }) {
  const { t } = useI18n();
  return (
    <Flex gap={4} wrap>
      <Tag color={statusColor[status]}>{t(`bill.status.${status}`)}</Tag>
      {overdue && <Tag color="error">{t("bill.overdue")}</Tag>}
    </Flex>
  );
}

/** Keuangan > Hutang Supplier (Plan §6.19): bills to check and pay, aging. */
export function Bills() {
  const { t, formatDate, formatMoney, formatNumber } = useI18n();
  const navigate = useNavigate();
  const status = useStatusFilter(STATUS_CHOICES, "unpaid");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [supplierId, setSupplierId] = useUrlFilter<string>("supplierId", "");

  const payables = useQuery({ queryKey: ["finance", "payables"], queryFn: () => unwrap(api.GET("/v1/finance/payables")) });
  const filters = {
    ...(status.value !== "all" ? { status: status.value } : {}),
    ...(supplierId ? { supplierId } : {}),
    ...search.query,
    ...range.query,
  };
  const list = useQuery({
    queryKey: ["finance", "bills", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/finance/bills", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  const ap = payables.data;
  const money = (n: number) => <span className="z-num">{n ? formatMoney(n) : "—"}</span>;

  const card = (title: string, value: number, extra: string, onClick?: () => void, danger?: boolean) => (
    <Col xs={12} lg={6}>
      <Card size="small" loading={payables.isPending} hoverable={!!onClick} onClick={onClick} style={{ height: "100%" }}>
        <Statistic title={title} value={formatMoney(value)} styles={danger && value > 0 ? { content: { color: "var(--ant-color-error)" } } : undefined} />
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {extra}
        </Typography.Text>
      </Card>
    </Col>
  );

  return (
    <>
      <Typography.Title level={2} style={{ margin: "0 0 16px", fontSize: 28 }}>
        {t("bill.title")}
      </Typography.Title>
      {ap && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {card(t("bill.outstanding"), ap.outstanding, t("bill.outstandingHelp"), () => status.set("unpaid"))}
          {card(t("bill.overdueTotal"), ap.overdue, t("bill.overdueBills", { n: formatNumber(ap.overdueBills) }), () => status.set("overdue"), true)}
          {card(t("bill.dueSoon"), ap.dueSoon, t("bill.dueSoonHelp"))}
          {card(t("bill.toCheck"), ap.draftsTotal, t("bill.toCheckHelp", { n: formatNumber(ap.drafts) }), () => status.set("draft"))}
        </Row>
      )}
      {ap && ap.credits > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("bill.creditsOpen", { amount: formatMoney(ap.credits), n: formatNumber(ap.creditNotes) })}
          action={
            <Button size="small" onClick={() => navigate("/app/finance/credits")}>
              {t("bill.creditsOpenView")}
            </Button>
          }
        />
      )}
      {ap && ap.aging.length > 0 && (
        <Card size="small" title={t("bill.aging")} style={{ marginBottom: 16 }}>
          <Table<Aging>
            size="small"
            rowKey="supplierId"
            pagination={ap.aging.length > 10 ? { pageSize: 10 } : false}
            dataSource={ap.aging}
            scroll={{ x: 900 }}
            onRow={(a) => ({ onClick: () => { setSupplierId(a.supplierId); status.set("unpaid"); }, style: { cursor: "pointer" } })}
            columns={[
              { title: t("po.supplier"), dataIndex: "supplierName" },
              { title: t("bill.agingCurrent"), dataIndex: "current", width: 140, align: "right", render: money },
              { title: t("bill.aging1to30"), dataIndex: "days1to30", width: 130, align: "right", render: money },
              { title: t("bill.aging31to60"), dataIndex: "days31to60", width: 130, align: "right", render: money },
              { title: t("bill.aging61to90"), dataIndex: "days61to90", width: 130, align: "right", render: money },
              { title: t("bill.agingOver90"), dataIndex: "daysOver90", width: 130, align: "right", render: money },
              { title: t("bill.outstanding"), dataIndex: "outstanding", width: 150, align: "right", render: (n: number) => <strong className="z-num">{formatMoney(n)}</strong> },
            ]}
          />
        </Card>
      )}
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("bill.search")} />
        <StatusFilter
          filter={status}
          options={[
            { value: "unpaid", label: t("bill.filter.unpaid") },
            { value: "draft", label: t("bill.filter.draft") },
            { value: "overdue", label: t("bill.overdue") },
            { value: "paid", label: t("bill.status.paid") },
            { value: "void", label: t("bill.status.void") },
            { value: "all", label: t("list.allStatuses") },
          ]}
        />
        <SupplierSelect value={supplierId || undefined} onChange={(id) => setSupplierId(id ?? "")} allowClear style={{ width: 240 }} />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/finance/bills/export"
          query={filters}
          filename={t("bill.title")}
          columns={[
            { key: "number", label: t("bill.number") },
            { key: "supplierName", label: t("po.supplier") },
            { key: "invoiceNo", label: t("bill.invoiceNo") },
            { key: "invoiceDate", label: t("bill.invoiceDate"), kind: "date" },
            { key: "dueDate", label: t("bill.dueDate"), kind: "date" },
            { key: "poNumber", label: t("po.number") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(BILL_STATUSES.map((v) => [v, t(`bill.status.${v}`)])) },
            { key: "total", label: t("po.total"), kind: "number" },
            { key: "paid", label: t("bill.paid"), kind: "number" },
            { key: "balance", label: t("bill.balance"), kind: "number" },
          ]}
        />
      </Flex>
      <Table<BillRow>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("bill.empty") }}
        onRow={(b) => ({ onClick: () => navigate(`/app/finance/bills/${b.id}`), style: { cursor: "pointer" } })}
        columns={[
          {
            title: t("bill.number"),
            dataIndex: "number",
            width: 150,
            render: (v: string, b) => (
              <span>
                <Typography.Text strong className="z-num">
                  {v}
                </Typography.Text>
                {b.poNumber && (
                  <>
                    <br />
                    <Typography.Text type="secondary" className="z-num" style={{ fontSize: 12 }}>
                      {b.poNumber}
                    </Typography.Text>
                  </>
                )}
              </span>
            ),
          },
          { title: t("po.supplier"), dataIndex: "supplierName" },
          { title: t("bill.invoiceNo"), dataIndex: "invoiceNo", width: 150, render: (v: string) => v || <Typography.Text type="secondary">—</Typography.Text> },
          { title: t("common.status"), dataIndex: "status", width: 190, render: (v: BillStatus, b) => <BillStatusTag status={v} overdue={b.overdue} /> },
          { title: t("bill.dueDate"), dataIndex: "dueDate", width: 130, render: (v: string) => formatDate(v) },
          { title: t("po.total"), dataIndex: "total", width: 150, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
          { title: t("bill.balance"), dataIndex: "balance", width: 150, align: "right", render: (n: number) => <strong className="z-num">{n ? formatMoney(n) : "—"}</strong> },
        ]}
      />
    </>
  );
}
